// billingService.js
// All billing API calls. Follows the same pattern as settingsService.js.

const parseResponse = async (response, fallbackMessage) => {
  let data = {};
  try {
    data = await response.json();
  } catch (_e) {
    data = {};
  }
  if (!response.ok) {
    const error = new Error(data?.error || fallbackMessage);
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return data;
};

const request = (authenticatedFetch, endpoint, options = {}, fallbackMessage = 'Request failed') =>
  authenticatedFetch(endpoint, options).then((res) => parseResponse(res, fallbackMessage));

export const billingService = {
  async getStatus(authenticatedFetch) {
    return request(authenticatedFetch, '/billing/status', {}, 'Couldn\'t load billing status');
  },

  async getOffer(authenticatedFetch) {
    return request(authenticatedFetch, '/billing/offer', {}, 'Couldn\'t load billing options');
  },

  async previewOnboardingSubscription(authenticatedFetch, billingDetails) {
    return request(
      authenticatedFetch,
      '/billing/onboarding/subscription-preview',
      { method: 'POST', body: JSON.stringify({ billingDetails: billingDetails || null }) },
      'Couldn\'t calculate the amount due'
    );
  },

  async createOnboardingSubscriptionIntent(authenticatedFetch, requestKey, billingDetails, paymentMethodId) {
    return request(
      authenticatedFetch,
      '/billing/onboarding/subscription-intent',
      {
        method: 'POST',
        body: JSON.stringify({
          requestKey: requestKey || null,
          billingDetails: billingDetails || null,
          paymentMethodId: paymentMethodId || null,
        }),
      },
      'Couldn\'t start secure payment'
    );
  },

  async confirmOnboardingSubscription(authenticatedFetch, subscriptionId) {
    return request(
      authenticatedFetch,
      '/billing/onboarding/subscription-confirmation',
      {
        method: 'POST',
        body: JSON.stringify({ subscriptionId: subscriptionId || null }),
      },
      'Couldn\'t confirm payment'
    );
  },

  async confirmOnboardingCheckout(authenticatedFetch, sessionId) {
    const encodedSessionId = encodeURIComponent(sessionId || '');
    return request(
      authenticatedFetch,
      `/billing/onboarding/checkout-session?sessionId=${encodedSessionId}`,
      {},
      'Couldn\'t confirm checkout'
    );
  },

  async createSetupIntent(authenticatedFetch, requestKey = null) {
    return request(
      authenticatedFetch,
      '/billing/setup-intent',
      { method: 'POST', body: JSON.stringify({ requestKey: requestKey || null }) },
      'Couldn\'t start payment setup'
    );
  },

  async cancel(authenticatedFetch) {
    return request(authenticatedFetch, '/billing/cancel', { method: 'POST', body: JSON.stringify({}) }, 'Couldn\'t cancel subscription');
  },

  async removePaymentMethod(authenticatedFetch) {
    return request(
      authenticatedFetch,
      '/billing/remove-payment-method',
      { method: 'POST', body: JSON.stringify({}) },
      'Couldn\'t remove payment method'
    );
  },

  async resubscribe(authenticatedFetch, paymentMethodId, billingDetails = null) {
    return request(
      authenticatedFetch,
      '/billing/resubscribe',
      {
        method: 'POST',
        body: JSON.stringify({
          paymentMethodId: paymentMethodId || null,
          billingDetails: billingDetails || null,
        }),
      },
      'Couldn\'t resubscribe'
    );
  },

  async openPortal(authenticatedFetch) {
    return request(
      authenticatedFetch,
      '/billing/portal',
      { method: 'POST', body: JSON.stringify({}) },
      'Couldn\'t open billing portal'
    );
  },

  async getInvoices(authenticatedFetch) {
    return request(authenticatedFetch, '/billing/invoices', {}, 'Couldn\'t load invoices');
  },
};
